import equal from "fast-deep-equal/es6";

import { EventClient } from "@formant/core/client/EventClient";
import { defined } from "@formant/core/common/defined";
import { PromiseLimit } from "@formant/core/common/PromiseLimit";
import { IEventStore } from "@formant/core/model/data/IEventStore";
import { duration } from "@formant/core/model/duration";
import { EventType } from "@formant/core/model/EventType";
import { IEvent } from "@formant/core/model/IEvent";
import { IEventCounts } from "@formant/core/model/IEventCounts";
import { IEventCountsByDevice } from "@formant/core/model/IEventCountsByDevice";
import { IEventFilter } from "@formant/core/model/IEventFilter";
import { IEventHistogram } from "@formant/core/model/IEventHistogram";
import { IEventOrAggregate } from "@formant/core/model/IEventOrAggregate";
import { IEventQuery } from "@formant/core/model/IEventQuery";
import { IIntervalEventFilter } from "@formant/core/model/IIntervalEventFilter";
import { IInterventionRequest } from "@formant/core/model/IInterventionRequest";
import { IInterventionTypeMap } from "@formant/core/model/IInterventionTypeMap";
import { Interval } from "@formant/core/model/Interval";
import { InterventionType } from "@formant/core/model/InterventionType";
import { ITagSets } from "@formant/core/model/ITagSets";
import { Severity } from "@formant/core/model/Severity";
import { StreamType } from "@formant/core/model/StreamType";
import { Uuid } from "@formant/core/model/Uuid";

import { interventionResponseClient } from "../clients";
import {
    materializeEvents,
    queryAggregateEvents,
    reaggregate,
} from "../utils/eventAggregates";
import { IAuthStore } from "./IAuthStore";
import { StoreCache } from "./StoreCache";

export class EventStore implements IEventStore {
    private queryCache = new StoreCache<IEventQuery, IEvent[] | false>({
        staleIntervalMs: 5 * duration.second,
        isEqual: equal,
    });
    private countCache = new StoreCache<IEventFilter, number>({
        staleIntervalMs: 5 * duration.second,
        isEqual: equal,
    });
    private countsCache = new StoreCache<IEventFilter, IEventCounts>({
        staleIntervalMs: 15 * duration.second,
        isEqual: equal,
    });

    private countsByDeviceCache = new StoreCache<
        IEventQuery,
        IEventCountsByDevice | undefined
    >({
        staleIntervalMs: 15 * duration.second,
        isEqual: equal,
    });
    private histogramCache = new StoreCache<
        IIntervalEventFilter,
        IEventHistogram
    >({
        staleIntervalMs: 15 * duration.second,
        isEqual: equal,
    });
    private cache = new StoreCache<Uuid, IEvent | false>();
    private eventTypeCache = new StoreCache<IEventFilter, EventType[]>();
    private userCache = new StoreCache<IEventFilter, Uuid[]>();
    private annotationTemplateCache = new StoreCache<IEventFilter, Uuid[]>();
    private deviceCache = new StoreCache<IEventFilter, Uuid[]>();
    private streamTypeCache = new StoreCache<IEventFilter, StreamType[]>();
    private streamNameCache = new StoreCache<IEventFilter, string[]>();
    private tagCache = new StoreCache<IEventFilter, ITagSets>();
    private severityCache = new StoreCache<IEventFilter, Severity[]>();

    private countsLimit = new PromiseLimit<IEventCounts>({
        name: "EventStore-countsLimit",
        concurrency: 2,
    });
    private countLimit = new PromiseLimit<number>({
        name: "EventStore-countLimit",
        concurrency: 2,
    });

    constructor(
        private eventClient: EventClient,
        private authStore: IAuthStore,
    ) {}

    query(query: IEventQuery): IEvent[] | undefined {
        return (
            this.queryCache.get(
                query,
                async () => await this.fetchList(query),
            ) || undefined
        );
    }

    async queryOrFetch(query: IEventQuery): Promise<IEvent[]> {
        const cached = this.query(query);
        if (cached) {
            return cached;
        }

        const fetched = await this.fetchList(query);
        this.queryCache.set(query, fetched);
        return fetched || [];
    }

    queryEventOrAggregates(
        query: IEventQuery,
        interval: Interval,
        start: Date,
        end: Date,
        millisecondsPerEvent: number,
    ): IEventOrAggregate[] | undefined {
        if (interval === "year") {
            interval = "month";
        }

        const aggregates =
            interval &&
            start &&
            end &&
            queryAggregateEvents(start, end, query, interval, _ =>
                this.histogram(_),
            );

        const eventsOrAggregates =
            aggregates &&
            materializeEvents(query, aggregates, millisecondsPerEvent, _ =>
                this.query(_),
            );

        const aggregatedEventsOrAggregates =
            eventsOrAggregates &&
            reaggregate(eventsOrAggregates, query, millisecondsPerEvent);

        return aggregatedEventsOrAggregates;
    }

    get(id: Uuid): IEvent | false | undefined {
        return this.cache.get(id, async () => await this.fetchGet(id));
    }

    hasNotifications(filter: IEventFilter): boolean | undefined {
        const counts = this.counts(filter);

        if (!counts) {
            return undefined;
        }
        return (
            (counts["intervention-request"] || 0) +
                (counts["datapoint-event"] || 0) +
                (counts.custom || 0) +
                (counts["triggered-event"] || 0) >
            0
        );
    }

    hasNotificationsByDevice(
        filter: IEventQuery,
        deviceId: Uuid,
    ): boolean | undefined {
        const result = this.countsByDevice(filter);

        if (!result) {
            return undefined;
        }

        const deviceCounts = result.counts[deviceId];

        if (!deviceCounts) {
            return undefined;
        }

        return (
            (deviceCounts["intervention-request"] || 0) +
                (deviceCounts["datapoint-event"] || 0) +
                (deviceCounts.custom || 0) +
                (deviceCounts["triggered-event"] || 0) >
            0
        );
    }

    count(filter: IEventFilter): number | undefined {
        return this.countCache.get(filter, () =>
            this.countLimit.run(async () =>
                this.eventClient.count(await this.authStore.token(), filter),
            ),
        );
    }

    counts(filter: IEventFilter): IEventCounts | undefined {
        return this.countsCache.get(filter, async () =>
            this.fetchCounts(filter),
        );
    }

    countsByDevice(filter: IEventFilter): IEventCountsByDevice | undefined {
        return this.countsByDeviceCache.get(filter, async () => {
            return this.fetchCountsByDevice(filter);
        });
    }

    refreshCountsByDevice(
        filter: IEventFilter,
        options: { lazy?: boolean } = {},
    ): Promise<void> {
        const { lazy = false } = options;
        return this.countsByDeviceCache.refresh(
            filter,
            async () => {
                return this.fetchCountsByDevice(filter);
            },
            { lazy },
        );
    }

    eventTypes(filter: IEventFilter): EventType[] | undefined {
        return this.eventTypeCache.get(filter, async () => {
            const { items } = await this.eventClient.eventTypes(
                await this.authStore.token(),
                filter,
            );
            this.eventTypeCache.set(filter, items);
            return items;
        });
    }

    users(filter: IEventFilter): Uuid[] | undefined {
        return this.userCache.get(filter, async () => {
            const { items } = await this.eventClient.users(
                await this.authStore.token(),
                filter,
            );
            this.userCache.set(filter, items);
            return items;
        });
    }

    annotationTemplates(filter: IEventFilter): Uuid[] | undefined {
        return this.annotationTemplateCache.get(filter, async () => {
            const { items } = await this.eventClient.annotationTemplates(
                await this.authStore.token(),
                filter,
            );
            this.userCache.set(filter, items);
            return items;
        });
    }

    devices(filter: IEventFilter): Uuid[] | undefined {
        return this.deviceCache.get(filter, async () => {
            const { items } = await this.eventClient.devices(
                await this.authStore.token(),
                filter,
            );
            this.deviceCache.set(filter, items);
            return items;
        });
    }

    streamTypes(filter: IEventFilter): StreamType[] | undefined {
        return this.streamTypeCache.get(filter, async () => {
            const { items } = await this.eventClient.streamTypes(
                await this.authStore.token(),
                filter,
            );
            this.streamTypeCache.set(filter, items);
            return items;
        });
    }

    streamNames(filter: IEventFilter): string[] | undefined {
        return this.streamNameCache.get(filter, async () => {
            const { items } = await this.eventClient.streamNames(
                await this.authStore.token(),
                filter,
            );
            this.streamNameCache.set(filter, items);
            return items;
        });
    }

    tags(filter: IEventFilter = {}): ITagSets | undefined {
        return this.tagCache.get(filter, async () => {
            const { tags } = await this.eventClient.tags(
                await this.authStore.token(),
                filter,
            );
            this.tagCache.set(filter, tags);
            return tags;
        });
    }

    severities(filter: IEventFilter = {}): Severity[] | undefined {
        return this.severityCache.get(filter, async () => {
            const { items } = await this.eventClient.severities(
                await this.authStore.token(),
                filter,
            );
            this.severityCache.set(filter, items);
            return items;
        });
    }

    histogram(filter: IIntervalEventFilter): IEventHistogram | undefined {
        return this.histogramCache.get(filter, async () =>
            this.eventClient.histogram(await this.authStore.token(), filter),
        );
    }

    async view(filter: IEventFilter): Promise<{ completed: boolean }> {
        const result = await this.eventClient.view(
            await this.authStore.token(),
            filter,
        );
        this.clear();
        return result;
    }

    async respond<T extends InterventionType>(
        intervention: IInterventionRequest<T>,
        data: IInterventionTypeMap[T]["response"],
    ) {
        const { interventionType } = intervention;

        await interventionResponseClient.create(await this.authStore.token(), {
            interventionId: defined(intervention.id),
            time: new Date().toISOString(),
            type: "intervention-response",
            message: intervention.message,
            tags: intervention.tags,
            deviceId: intervention.deviceId,
            interventionType,
            data,
        });
        await this.view({ id: defined(intervention.id) });
        this.clear();
    }

    public async fetchCountsByDevice(
        filter: IEventFilter,
    ): Promise<IEventCountsByDevice | undefined> {
        const value = await this.eventClient.countsByDevice(
            await this.authStore.token(),
            filter,
        );
        return value;
    }

    clear() {
        this.queryCache.clear();
        this.countCache.clear();
        this.countsCache.clear();
        this.countsByDeviceCache.clear();
        this.histogramCache.clear();
        this.cache.clear();
        this.eventTypeCache.clear();
    }

    async refreshCounts(filter: IEventFilter) {
        await this.countsCache.refresh(filter, async () =>
            this.fetchCounts(filter),
        );
    }

    async refreshList(filter: IEventFilter) {
        await this.queryCache.refresh(filter, async () =>
            this.fetchList(filter),
        );
    }

    async fetchGet(id: Uuid): Promise<IEvent | false> {
        const event = await this.eventClient.get(
            await this.authStore.token(),
            id,
        );
        const value = event || false;
        this.cache.set(id, value);
        return value;
    }

    async fetchList(query: IEventQuery): Promise<IEvent[] | false> {
        const events = await this.eventClient.query(
            await this.authStore.token(),
            query,
        );

        const value = events || false;
        this.queryCache.set(query, value);
        return value;
    }

    private async fetchCounts(filter: IEventFilter): Promise<IEventCounts> {
        const value = await this.countsLimit.run(
            async () =>
                await this.eventClient.counts(
                    await this.authStore.token(),
                    filter,
                ),
        );
        this.countsCache.set(filter, value);
        return value;
    }
}
